import { Extension, type JSONContent } from '@tiptap/core'
import { Fragment, Slice } from '@tiptap/pm/model'
import { Plugin } from '@tiptap/pm/state'
import { type Editor } from '@tiptap/react'

// Blocks of a card move by their handle: within the card (ProseMirror's own
// drop), into another card, or out onto the board, where they become a new
// card. Out of the card they are moved, not copied, as within it.

export type BlockDrag = {
  cardId: string
  editor: Editor
  from: number
  to: number
  blocks: JSONContent[]
}

let current: BlockDrag | null = null

/** Note the blocks a handle started dragging; cleared when the drag ends. */
export function startBlockDrag(d: BlockDrag) {
  current = d
}

export function endBlockDrag(editor: Editor) {
  if (current?.editor === editor) current = null
}

/** The blocks being dragged by a handle right now, if any. */
export function activeBlockDrag(): BlockDrag | null {
  return current
}

/** Take the dragged blocks out of their card (they have landed elsewhere). */
export function removeFromSource(d: BlockDrag) {
  if (d.editor.isDestroyed) return
  d.editor.chain().deleteRange({ from: d.from, to: d.to }).run()
}

/** Put blocks taken out by a drag back where they were. */
export function restoreToSource(d: BlockDrag) {
  if (d.editor.isDestroyed) return
  d.editor.chain().insertContentAt(d.from, d.blocks).run()
}

/** The card a drag started in (by a handle, or selected text), while it lasts. */
let dragSource: Editor | null = null

/**
 * For every card: what is dragged in from another card lands here and leaves
 * that card, once. Tiptap does this too, but from each of its paste-rule
 * plugins, so the source lost one more passage per rule; this runs first and
 * ends the drop. A drag within one card is ProseMirror's own move.
 */
export const BlockDrops = Extension.create({
  name: 'blockDrops',
  priority: 10000,
  addProseMirrorPlugins() {
    const editor = this.editor
    return [
      new Plugin({
        view(view) {
          const onStart = (e: DragEvent) => {
            if (e.target instanceof Node && view.dom.parentElement?.contains(e.target))
              dragSource = editor
          }
          const onEnd = () => {
            if (dragSource === editor) dragSource = null
          }
          window.addEventListener('dragstart', onStart)
          window.addEventListener('dragend', onEnd)
          return {
            destroy() {
              window.removeEventListener('dragstart', onStart)
              window.removeEventListener('dragend', onEnd)
            },
          }
        },
        props: {
          handleDOMEvents: {
            drop(view, event) {
              const src = dragSource
              if (!src || src.view === view || src.isDestroyed || !view.editable) return false
              const at = view.posAtCoords({ left: event.clientX, top: event.clientY })
              if (!at) return false
              event.preventDefault()
              const blocks = current?.editor === src ? current : null
              if (blocks) {
                const $p = view.state.doc.resolve(at.pos)
                // Between top-level blocks, never before the title.
                const pos = $p.depth
                  ? $p.after(1)
                  : Math.max(at.pos, view.state.doc.firstChild?.nodeSize ?? 0)
                view.dispatch(
                  view.state.tr.insert(pos, Fragment.fromJSON(view.state.schema, blocks.blocks)),
                )
                removeFromSource(blocks)
                current = null
              } else {
                // Each editor has its own schema: carry the slice over as JSON.
                const taken = src.state.selection.content()
                if (!taken.size) return true
                const slice = Slice.fromJSON(view.state.schema, taken.toJSON())
                view.dispatch(view.state.tr.replaceRange(at.pos, at.pos, slice))
                src.commands.deleteSelection()
              }
              dragSource = null
              return true
            },
          },
        },
      }),
    ]
  },
})
